import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, Surface, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { NetworkLink } from '@app/router/constants';
import { CardWithActions } from '@app/components/CardWithActions';
import { useNavigate } from '@app/hooks';
import { selectConfig, selectNetwork, updateServiceSubType } from '@app/redux';

import { OntMode, UpdateCommandError } from '@mfe/shared/schema-types';

import { CommandState } from '../../../../../platform/src/server/dataSources/PSM/schemaTypes';
import { GeneralErrorAlert } from './GeneralErrorAlert';
import { SubTypeUpdateLoading } from './SubTypeUpdateLoading';
import { SwitchAlert } from './SwitchAlert';

import styles from './styles.module.scss';

export const BridgeMode = () => {
  const { t } = useTranslation('Network', { keyPrefix: 'networkManagement' });
  const dispatch = useDispatch();
  const { goTo, replace } = useNavigate();

  const { enableBridgeMode } = useSelector(selectConfig);
  const { ontModeSwitchLoading, updateError, updateStatus } = useSelector(selectNetwork);

  const isGeneralError = updateError && updateError !== UpdateCommandError.RateLimitExceededError;

  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    if (!enableBridgeMode) {
      replace(NetworkLink.Network);
    }
  }, []);

  useEffect(() => {
    const hasSucceeded = updateStatus === CommandState.Succeeded;
    const hasExceededRateLimit = updateError === UpdateCommandError.RateLimitExceededError;

    if (hasSucceeded || hasExceededRateLimit) {
      goTo(NetworkLink.Network);
    }
  }, [updateStatus, isGeneralError]);

  return (
    <>
      <Surface pt="48px" variant="secondary" className={styles.header}>
        <Txt variant="heading3" color="regular" data-cy="network-bridge-header" mb="48px">
          {t('switchToBridge.title')}
        </Txt>
      </Surface>

      {ontModeSwitchLoading ? (
        <SubTypeUpdateLoading />
      ) : (
        <Surface variant="secondary" className={styles.content}>
          {isGeneralError && <GeneralErrorAlert />}
          <SwitchAlert />
          <CardWithActions
            className={styles.bridgeContentCard}
            primaryActionLabel={t('switchToBridge.primaryAction')}
            primaryActionDisabled={!isChecked}
            handlePrimaryActionClick={() => dispatch(updateServiceSubType({ ontMode: OntMode.Bridge }))}
            secondaryActionLabel={t('switchToBridge.secondaryAction')}
          >
            <Txt variant="bodySmallRegular">{t('switchToBridge.description')}</Txt>
            <Checkbox
              id="confirm-bridge-mode-switch"
              label={t('switchToBridge.confirmSwitch')}
              name="confirm-bridge-mode-switch"
              checked={isChecked}
              onChange={() => setIsChecked(!isChecked)}
            />
          </CardWithActions>
        </Surface>
      )}
    </>
  );
};
